import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Wallet, Activity, Database, AlertTriangle, Loader2, Mail, Send, Globe, Server, TrendingUp } from "lucide-react";

const eur = (n) => `${Number(n || 0).toFixed(2)} €`;
const PLAN_NAMES = { starter: "Essentiel", pro: "Premium", business: "Expert", essai: "Essai", superadmin: "Superadmin", aucun: "Sans abo.", inconnu: "Inconnu" };

export default function AdminCosts() {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);
  const [caps, setCaps] = useState({ monthly_cap: 40, daily_fuse: 5 });
  const [rep, setRep] = useState(null);
  const [repErr, setRepErr] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () =>
    api.get("/admin/cost/overview").then((r) => {
      setD(r.data);
      setCaps({ monthly_cap: r.data.monthly_cap_eur, daily_fuse: r.data.daily_fuse_eur });
    }).catch(() => toast.error("Chargement impossible"));

  const loadReport = () =>
    api.get("/admin/cost/monthly-report").then((r) => { setRep(r.data); setRepErr(false); })
      .catch(() => { setRepErr(true); toast.error("Rapport indisponible"); });

  useEffect(() => { load(); loadReport(); }, []);

  const sendReport = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/cost/monthly-report/send");
      if (r.data?.sent) toast.success("Rapport envoyé par e-mail");
      else toast.info("Rapport généré (e-mail non confirmé)");
      await loadReport();
    } catch { toast.error("Échec de l'envoi du rapport"); }
    finally { setSending(false); }
  };

  const patch = async (payload, label) => {
    setSaving(true);
    try { await api.post("/admin/cost/settings", payload); toast.success(label || "Enregistré"); await load(); }
    catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  if (!d) return <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const monthPct = Math.min(100, (d.spend.month_eur / (d.monthly_cap_eur || 1)) * 100);
  const dayPct = Math.min(100, (d.spend.day_eur / (d.daily_fuse_eur || 1)) * 100);

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-8" data-testid="admin-costs">
      <div>
        <h1 className="font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">Coûts &amp; Protections Apify</h1>
        <p className="mt-2 text-slate-500">Sources gratuites d'abord · cache &amp; base centrale · Apify en dernier recours, plafonné.</p>
      </div>

      {/* Interrupteurs de sécurité */}
      <Card className="p-6" data-testid="cost-switches">
        <div className="flex flex-wrap items-center gap-8">
          <div className="flex items-center gap-3">
            <Switch data-testid="toggle-simulation" checked={d.simulation}
              onCheckedChange={(v) => patch({ simulation: v }, v ? "Mode simulation activé" : "Mode simulation désactivé")} disabled={saving} />
            <div>
              <p className="font-semibold text-[#0F172A]">Mode simulation</p>
              <p className="text-xs text-slate-500">Aucune collecte Apify réelle (aucune dépense).</p>
            </div>
            {d.simulation && <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">ON — 0 € garanti</span>}
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="toggle-killswitch" checked={d.killswitch}
              onCheckedChange={(v) => patch({ killswitch: v }, v ? "Kill-switch activé" : "Kill-switch désactivé")} disabled={saving} />
            <div>
              <p className="font-semibold text-[#0F172A]">Kill-switch</p>
              <p className="text-xs text-slate-500">Suspend toute collecte Apify immédiatement.</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Budgets € */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="p-6" data-testid="budget-month">
          <div className="flex items-center gap-2 text-slate-600"><Wallet className="h-4 w-4" /> Dépense du mois</div>
          <p className="mt-2 text-3xl font-bold text-[#0F172A]">{eur(d.spend.month_eur)} <span className="text-base font-normal text-slate-400">/ {eur(d.monthly_cap_eur)}</span></p>
          <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${monthPct > 90 ? "bg-red-500" : monthPct > 75 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${monthPct}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-500">Projection fin de mois : <b>{eur(d.projected_month_eur)}</b></p>
        </Card>
        <Card className="p-6" data-testid="budget-day">
          <div className="flex items-center gap-2 text-slate-600"><Activity className="h-4 w-4" /> Fusible journalier</div>
          <p className="mt-2 text-3xl font-bold text-[#0F172A]">{eur(d.spend.day_eur)} <span className="text-base font-normal text-slate-400">/ {eur(d.daily_fuse_eur)}</span></p>
          <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${dayPct > 90 ? "bg-red-500" : dayPct > 75 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${dayPct}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-500">Annonces payantes aujourd'hui : {d.item_budget?.used ?? 0} / {d.item_budget?.limit ?? "—"}</p>
        </Card>
      </div>

      {/* Réglage des plafonds */}
      <Card className="p-6" data-testid="cap-editor">
        <div className="flex flex-wrap items-end gap-4">
          <div><label className="text-xs font-medium text-slate-600">Plafond mensuel (€)</label>
            <Input data-testid="cap-monthly" type="number" min="0" value={caps.monthly_cap} onChange={(e) => setCaps({ ...caps, monthly_cap: e.target.value })} className="mt-1 h-10 w-32" /></div>
          <div><label className="text-xs font-medium text-slate-600">Fusible journalier (€)</label>
            <Input data-testid="cap-daily" type="number" min="0" value={caps.daily_fuse} onChange={(e) => setCaps({ ...caps, daily_fuse: e.target.value })} className="mt-1 h-10 w-32" /></div>
          <Button data-testid="cap-save" onClick={() => patch({ monthly_cap: parseFloat(caps.monthly_cap), daily_fuse: parseFloat(caps.daily_fuse) }, "Plafonds mis à jour")} disabled={saving}
            className="h-10 bg-[#0F172A]">Enregistrer les plafonds</Button>
        </div>
      </Card>

      {/* Journal du mois */}
      <Card className="p-6" data-testid="cost-journal">
        <div className="mb-3 flex items-center gap-2 text-slate-600"><Database className="h-4 w-4" /> Journal de collecte (mois en cours)</div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl bg-slate-50 p-3"><p className="text-2xl font-bold text-[#0F172A]">{d.journal.real_runs}</p><p className="text-xs text-slate-500">Runs Apify réels</p></div>
          <div className="rounded-xl bg-emerald-50 p-3"><p className="text-2xl font-bold text-emerald-700">{d.journal.simulated_runs}</p><p className="text-xs text-slate-500">Simulés (0 €)</p></div>
          <div className="rounded-xl bg-sky-50 p-3"><p className="text-2xl font-bold text-sky-700">{d.journal.cache_hits}</p><p className="text-xs text-slate-500">Servis par cache/BD</p></div>
        </div>
        {Object.keys(d.journal.by_country || {}).length > 0 && (
          <table className="mt-4 w-full text-sm"><thead><tr className="text-left text-xs uppercase text-slate-400">
            <th className="py-1">Pays</th><th>Runs réels</th><th>Annonces</th><th>Coût €</th></tr></thead>
            <tbody>{Object.entries(d.journal.by_country).map(([cc, v]) => (
              <tr key={cc} className="border-t border-slate-100"><td className="py-1.5 font-medium">{cc}</td><td>{v.runs}</td><td>{v.results}</td><td>{eur(v.eur)}</td></tr>
            ))}</tbody></table>
        )}
        <p className="mt-3 text-xs text-slate-500">Recherches RADAR (BD/cache/gratuit) aujourd'hui : <b>{d.radar_searches_today}</b> — sans coût Apify.</p>
      </Card>

      {/* Projections abonnés */}
      <Card className="p-6" data-testid="cost-projections">
        <div className="mb-3 flex items-center gap-2 text-slate-600"><ShieldCheck className="h-4 w-4" /> Projection mensuelle Apify (plafonnée)</div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {d.projections.map((p) => (
            <div key={p.subscribers} className="rounded-xl border border-slate-200 p-3 text-center" data-testid={`proj-${p.subscribers}`}>
              <p className="text-lg font-bold text-[#0F172A]">{p.subscribers} abonnés</p>
              <p className="text-sm text-emerald-700">≈ {eur(p.estimated_eur)}</p>
              <p className="text-[11px] text-slate-400">plafonné à {eur(p.capped_at)}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
          Coût/annonce provisoire (recalculé sur facturation réelle). Grâce à la base centrale partagée + cache, le coût est sous-linéaire et ne peut jamais dépasser le plafond mensuel.
        </div>
      </Card>

      {/* Rapport mensuel */}
      <Card className="p-6" data-testid="monthly-report">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-slate-600">
            <Mail className="h-4 w-4" /> Rapport mensuel — {rep?.month || "mois en cours"}
            <span className="ml-1 text-xs text-slate-400">envoyé automatiquement le 1ᵉʳ à kazar.hamid@live.fr</span>
          </div>
          <Button data-testid="report-send-btn" onClick={sendReport} disabled={sending} className="h-9 bg-[#0F172A]">
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Envoyer le rapport maintenant
          </Button>
        </div>

        {!rep ? (
          <div className="mt-4 flex h-24 items-center justify-center text-slate-400" data-testid="report-loading">
            {repErr ? <span className="text-sm text-slate-500">Rapport indisponible pour le moment.</span> : <Loader2 className="h-5 w-5 animate-spin" />}
          </div>
        ) : (
          <div className="mt-4 space-y-5" data-testid="report-preview">
            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl bg-slate-50 p-3" data-testid="report-spend">
                <p className="text-xs text-slate-500">Dépense Apify</p>
                <p className="text-xl font-bold text-[#0F172A]">{eur(rep.total_spend_eur)}</p></div>
              <div className="rounded-xl bg-slate-50 p-3" data-testid="report-searches">
                <p className="text-xs text-slate-500">Recherches totales</p>
                <p className="text-xl font-bold text-[#0F172A]">{rep.total_searches}</p></div>
              <div className="rounded-xl bg-slate-50 p-3" data-testid="report-avg">
                <p className="text-xs text-slate-500">Coût moyen / utilisateur</p>
                <p className="text-xl font-bold text-[#0F172A]">{eur(rep.avg_cost_per_user_eur)}</p>
                <p className="text-[11px] text-slate-400">{rep.active_users} actif(s)</p></div>
              <div className="rounded-xl bg-slate-50 p-3" data-testid="report-runs">
                <p className="text-xs text-slate-500">Runs réels / simulés / cache</p>
                <p className="text-xl font-bold text-[#0F172A]">{rep.real_runs} / {rep.simulated_runs} / {rep.cache_hits}</p></div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              {/* Recherches par formule */}
              <div data-testid="report-by-plan">
                <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Database className="h-4 w-4" /> Recherches par formule</p>
                <div className="space-y-1.5">
                  {Object.entries(rep.by_plan || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm">
                      <span className="text-slate-600">{PLAN_NAMES[k] || k}</span><span className="font-semibold text-[#0F172A]">{v}</span></div>
                  ))}
                  {Object.keys(rep.by_plan || {}).length === 0 && <p className="text-xs text-slate-400">Aucune recherche ce mois.</p>}
                </div>
              </div>
              {/* Pays les plus recherchés */}
              <div data-testid="report-countries">
                <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Globe className="h-4 w-4" /> Pays les plus recherchés</p>
                <div className="space-y-1.5">
                  {(rep.top_countries || []).map((c) => (
                    <div key={c.country} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm">
                      <span className="text-slate-600">{c.country}</span>
                      <span className="text-slate-500">{c.results} annonces · <b className="text-[#0F172A]">{eur(c.eur)}</b></span></div>
                  ))}
                  {(rep.top_countries || []).length === 0 && <p className="text-xs text-slate-400">—</p>}
                </div>
              </div>
              {/* Acteurs Apify */}
              <div data-testid="report-actors">
                <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Server className="h-4 w-4" /> Acteurs Apify les plus consommateurs</p>
                <div className="space-y-1.5">
                  {(rep.top_actors || []).map((a) => (
                    <div key={a.actor} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm">
                      <span className="truncate text-slate-600">{a.actor}</span>
                      <span className="text-slate-500 whitespace-nowrap">{a.runs} runs · <b className="text-[#0F172A]">{eur(a.eur)}</b></span></div>
                  ))}
                  {(rep.top_actors || []).length === 0 && <p className="text-xs text-slate-400">—</p>}
                </div>
              </div>
              {/* Alertes & blocages */}
              <div data-testid="report-alerts">
                <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><AlertTriangle className="h-4 w-4" /> Alertes & blocages</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5"><span className="text-slate-600">Alertes de seuil (75/90 %)</span><span className="font-semibold text-[#0F172A]">{rep.alerts_triggered}</span></div>
                  <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5"><span className="text-slate-600">Runs bloqués (plafond/fusible)</span><span className="font-semibold text-[#0F172A]">{rep.blocked}</span></div>
                </div>
              </div>
            </div>

            {/* Rentabilité */}
            <div className="rounded-xl bg-emerald-50 p-4" data-testid="report-profitability">
              <p className="flex items-center gap-2 text-sm font-semibold text-emerald-800"><TrendingUp className="h-4 w-4" /> Estimation de rentabilité</p>
              <p className="mt-1 text-2xl font-bold text-emerald-900">
                {(rep.profit_eur || 0) > 0 ? "🟢 Rentable" : (rep.profit_eur || 0) < 0 ? "🔴 Déficitaire" : "⚪ Neutre"}
                {rep.margin_pct != null && <span className="text-base font-medium text-emerald-700"> · marge {rep.margin_pct} %</span>}
              </p>
              <p className="mt-1 text-sm text-emerald-700">
                Revenus abonnements estimés : <b>{eur(rep.revenue_eur)}</b> — Coût Apify : <b>{eur(rep.total_spend_eur)}</b> — Résultat net estimé : <b>{eur(rep.profit_eur)}</b>
              </p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
